import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FileText, 
  Download, 
  Search,
  User,
  Loader2,
  FileCheck,
  Sparkles
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import jsPDF from "jspdf";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RiskBadge({ band, score }) {
  const config = {
    RED: { dot: "risk-dot-red", text: "text-red-400" },
    AMBER: { dot: "risk-dot-amber", text: "text-amber-400" },
    GREEN: { dot: "risk-dot-green", text: "text-emerald-400" }
  };
  const { dot, text } = config[band] || config.GREEN;

  return (
    <div className="flex items-center gap-3">
      <div className={`risk-dot ${dot}`} />
      <span className={`text-sm font-bold font-mono-data ${text}`}>{score}</span>
    </div>
  );
}

export default function Reports() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (client) => {
    setGenerating(client.clientId);
    
    try {
      const response = await axios.get(`${API}/reports/${client.clientId}`);
      const reportData = response.data;
      
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;

      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 50, 'F');
      
      // Accent line
      doc.setFillColor(99, 102, 241);
      doc.rect(0, 50, pageWidth, 2, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('DUE DILIGENCE REPORT', 20, 30);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 42);
      doc.text(`Report ID: ${reportData.reportId?.slice(0, 8)}`, pageWidth - 50, 42);

      y = 65;
      doc.setTextColor(30, 41, 59);

      // Client Info Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('CLIENT INFORMATION', 20, y);
      y += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`Name: ${client.name}`, 20, y);
      y += 6;
      doc.text(`Email: ${client.email || 'N/A'}`, 20, y);
      y += 6;
      doc.text(`Country: ${client.address?.country || 'N/A'}`, 20, y);
      y += 6;
      if (client.business?.companyName) {
        doc.text(`Company: ${client.business.companyName}`, 20, y);
        y += 6;
      }
      
      // Risk Assessment Section
      y += 12;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('RISK ASSESSMENT', 20, y);
      y += 12;

      // Risk Score Box
      const riskColor = client.band === 'RED' ? [239, 68, 68] : 
                        client.band === 'AMBER' ? [245, 158, 11] : [16, 185, 129];
      doc.setFillColor(...riskColor);
      doc.roundedRect(20, y, 50, 28, 4, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text(`${client.riskScore}`, 32, y + 18);
      doc.setFontSize(10);
      doc.text('/100', 50, y + 18);
      
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(14);
      doc.text(`Risk Level: ${client.band}`, 80, y + 12);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`Status: ${client.status?.replace('_', ' ').toUpperCase() || 'N/A'}`, 80, y + 22);
      
      y += 40;

      // Risk Factors
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('SCREENING RESULTS', 20, y);
      y += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      client.factors?.forEach((factor) => {
        doc.setTextColor(factor.hit ? 239 : 16, factor.hit ? 68 : 185, factor.hit ? 68 : 129);
        const status = factor.hit ? '●' : '○';
        const statusText = factor.hit ? `${factor.name}: FLAGGED (+${factor.points}pts)` : `${factor.name}: CLEAR`;
        doc.text(`${status} ${statusText}`, 25, y);
        y += 6;
        if (factor.details && factor.hit) {
          doc.setTextColor(100, 116, 139);
          doc.setFontSize(8);
          doc.text(`   ${factor.source} - ${factor.details}`, 25, y);
          doc.setFontSize(10);
          y += 6;
        }
      });

      // Recommendation
      y += 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('AI RECOMMENDATION', 20, y);
      y += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      const recommendation = client.recommendation || 'Standard onboarding procedures apply.';
      const splitRec = doc.splitTextToSize(recommendation, pageWidth - 40);
      doc.text(splitRec, 20, y);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('DueDiligence AI Platform - Confidential', 20, 285);
      doc.text(`Page 1 of 1`, pageWidth - 30, 285);

      const fileName = `${client.name.replace(/\s+/g, '_')}_DueDiligence_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success(`Report downloaded: ${fileName}`);
    } catch (error) {
      toast.error("Failed to generate report");
      console.error(error);
    } finally {
      setGenerating(null);
    }
  };

  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-6"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20">
            <FileCheck className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Report Generation</h2>
            <p className="text-sm text-slate-500">Generate and download due diligence reports for any client</p>
          </div>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="glass-card rounded-2xl border-0">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients by name or email..."
                className="pl-11 input-premium h-11 rounded-xl"
                data-testid="search-reports"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Clients Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="glass-card rounded-2xl border-0 overflow-hidden">
          <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20">
                <FileText className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Available Reports</h3>
                <p className="text-xs text-slate-500">{filteredClients.length} clients</p>
              </div>
            </div>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <div className="inline-flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Loading clients...
                </div>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No clients found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Client</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Risk</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Date</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client, index) => (
                      <motion.tr 
                        key={client.clientId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="table-row-premium"
                        data-testid={`report-row-${client.clientId}`}
                      >
                        <TableCell className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                              <User className="h-4 w-4 text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{client.name}</p>
                              <p className="text-xs text-slate-500">{client.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <RiskBadge band={client.band} score={client.riskScore} />
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
                            client.status === 'approved' 
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                              : client.status === 'edd_required' 
                                ? 'bg-red-500/10 text-red-400 border border-red-500/20' 
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          }`}>
                            {client.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-400 font-mono-data">
                          {new Date(client.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm"
                            onClick={() => generatePDF(client)}
                            disabled={generating === client.clientId}
                            className="btn-premium text-white rounded-xl text-xs px-4"
                            data-testid={`download-report-${client.clientId}`}
                          >
                            {generating === client.clientId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="h-3.5 w-3.5 mr-2" />
                                Download
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
